import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  strategyPricingApi,
  type StrategyPricingAdminResponse,
  type StrategyPricingOverrideDto,
  type StrategyPricingVersionDto,
  type UpdateStrategyPricingRequest,
} from "@/lib/api";

const THIRTY_SECONDS = 30 * 1000;

/**
 * SFWL-014: query keys for the strategy-pricing admin surface, keyed per
 * strategy (mirrors `blockAccountsKeys.exportConfig`'s per-tenant keying).
 */
const strategyPricingKeys = {
  all: ["strategy-pricing"] as const,
  detail: (strategyId: string) => ["strategy-pricing", strategyId] as const,
};

/**
 * SFWL-014: admin pricing surface for a single strategy — live version +
 * full history, plus the close-and-insert PUT. Disabled until a
 * `strategyId` is provided (route param).
 *
 * The server enforces `SovrunAdminGuard` on both GET and PUT; a non-admin
 * session gets a 403 `Error` from `handleResponse` on the first call, which
 * `error` surfaces to the caller (toast is the page's responsibility — no
 * client-side role plumbing here, mirrors `useBlockAccounts`/`useExportConfig`).
 */
export function useStrategyPricing(strategyId?: string) {
  const queryClient = useQueryClient();

  const query = useQuery<StrategyPricingAdminResponse>({
    queryKey: strategyPricingKeys.detail(strategyId ?? ""),
    queryFn: () => strategyPricingApi.getStrategyPricing(strategyId as string),
    enabled: !!strategyId,
    staleTime: THIRTY_SECONDS,
    // This surface is SovrunAdminGuard-gated end-to-end (incl. GET) and
    // always 403s for non-admins; handleResponse discards response.status,
    // so a status-aware retry predicate isn't available without a client
    // refactor (see useBlockAccounts). retry:false avoids React Query's
    // default 3-retry storm on every non-admin page load.
    retry: false,
  });

  const setPricingMutation = useMutation({
    mutationFn: (data: UpdateStrategyPricingRequest) =>
      strategyPricingApi.setStrategyPricing(strategyId as string, data),
    onSuccess: () => {
      if (strategyId) {
        queryClient.invalidateQueries({
          queryKey: strategyPricingKeys.detail(strategyId),
        });
      }
    },
  });

  return {
    current: query.data?.current ?? null,
    history: query.data?.history ?? [],
    isLoading: query.isPending,
    error: query.error as Error | null,
    refetch: query.refetch,

    setPricing: setPricingMutation.mutateAsync,
    isSaving: setPricingMutation.isPending,
  };
}

export type {
  StrategyPricingAdminResponse,
  StrategyPricingOverrideDto,
  StrategyPricingVersionDto,
  UpdateStrategyPricingRequest,
};
